import { lazy } from 'react';
import { createLazyComponent } from '@/utils/bundleOptimization';
import { toast } from '@/hooks/use-toast';

const handleLazyError = (err: unknown, modulePath: string) => {
  // eslint-disable-next-line no-console
  console.error(`Failed to load module: ${modulePath}`, err);
  toast({
    title: 'Module load failed',
    description: 'Please hard refresh (Ctrl/Cmd+Shift+R) and try again.',
    variant: 'destructive',
  });
};

// ============= LAZY ROUTE COMPONENTS =============
// Optimized lazy loading with performance tracking and preloading

// Auth Routes - Lightweight bundle
export const LazyLoginPage = lazy(() =>
  import('@/pages/auth/Login').catch((err) => {
    handleLazyError(err, '@/pages/auth/Login');
    throw err;
  })
);
export const LazyCompleteProfilePage = lazy(() =>
  import('@/pages/auth/CompleteProfile').catch((err) => {
    handleLazyError(err, '@/pages/auth/CompleteProfile');
    throw err;
  })
);
export const LazyForgotPasswordPage = lazy(() =>
  import('@/pages/auth/ForgotPassword').catch((err) => {
    handleLazyError(err, '@/pages/auth/ForgotPassword');
    throw err;
  })
);
export const LazyResetPasswordPage = lazy(() =>
  import('@/pages/auth/ResetPassword').catch((err) => {
    handleLazyError(err, '@/pages/auth/ResetPassword');
    throw err;
  })
);
export const LazyPendingReviewPage = lazy(() =>
  import('@/pages/auth/PendingReview').catch((err) => {
    handleLazyError(err, '@/pages/auth/PendingReview');
    throw err;
  })
);
export const LazyProfileRejectedPage = lazy(() =>
  import('@/pages/auth/ProfileRejected').catch((err) => {
    handleLazyError(err, '@/pages/auth/ProfileRejected');
    throw err;
  })
);
export const LazyAccessRequestPage = lazy(() =>
  import('@/pages/AccessRequest').catch((err) => {
    handleLazyError(err, '@/pages/AccessRequest');
    throw err;
  })
);

// Main Application Routes - Core bundle
export const LazyIndexPage = lazy(() =>
  import('@/pages/Index').catch((err) => {
    handleLazyError(err, '@/pages/Index');
    throw err;
  })
);
export const LazyProfilePage = lazy(() =>
  import('@/pages/Profile').catch((err) => {
    handleLazyError(err, '@/pages/Profile');
    throw err;
  })
);
export const LazyNotFoundPage = lazy(() =>
  import('@/pages/NotFound').catch((err) => {
    handleLazyError(err, '@/pages/NotFound');
    throw err;
  })
);
export const LazyDebugPage = lazy(() =>
  import('@/pages/Debug').catch((err) => {
    handleLazyError(err, '@/pages/Debug');
    throw err;
  })
);

// Marketplace Routes - Heavy bundle (charts, filters, etc.)
export const LazyMarketplaceBrowse = lazy(() =>
  import('@/pages/Marketplace/Browse').catch((err) => {
    handleLazyError(err, '@/pages/Marketplace/Browse');
    throw err;
  })
);
export const LazyMarketplaceUltraFastBrowse = lazy(() =>
  import('@/pages/Marketplace/UltraFastBrowse').catch((err) => {
    handleLazyError(err, '@/pages/Marketplace/UltraFastBrowse');
    throw err;
  })
);
export const LazyMarketplaceDetail = lazy(() =>
  import('@/pages/MarketplaceDetail').catch((err) => {
    handleLazyError(err, '@/pages/MarketplaceDetail');
    throw err;
  })
);
export const LazyMarketplaceSettings = lazy(() =>
  import('@/pages/MarketplaceSettings').catch((err) => {
    handleLazyError(err, '@/pages/MarketplaceSettings');
    throw err;
  })
);
export const LazyProductDetails = lazy(() =>
  import('@/pages/ProductDetails').catch((err) => {
    handleLazyError(err, '@/pages/ProductDetails');
    throw err;
  })
);
export const LazyOrderManagement = lazy(() =>
  import('@/pages/OrderManagement').catch((err) => {
    handleLazyError(err, '@/pages/OrderManagement');
    throw err;
  })
);

// Admin Routes - Heavy bundle (tables, forms, etc.)
export const LazyAdminDashboard = lazy(() =>
  import('@/pages/admin/Dashboard').catch((err) => {
    handleLazyError(err, '@/pages/admin/Dashboard');
    throw err;
  })
);
export const LazyAdminUsers = lazy(() =>
  import('@/pages/admin/Users/index').catch((err) => {
    handleLazyError(err, '@/pages/admin/Users/index');
    throw err;
  })
);
export const LazyAdminCultivars = lazy(() =>
  import('@/pages/admin/Cultivars/index').catch((err) => {
    handleLazyError(err, '@/pages/admin/Cultivars/index');
    throw err;
  })
);
export const LazyAdminBatches = lazy(() =>
  import('@/pages/admin/Batches/index').catch((err) => {
    handleLazyError(err, '@/pages/admin/Batches/index');
    throw err;
  })
);
export const LazyAdminHardware = lazy(() =>
  import('@/pages/admin/Hardware/index').catch((err) => {
    handleLazyError(err, '@/pages/admin/Hardware/index');
    throw err;
  })
);
export const LazyAdminProducts = lazy(() =>
  import('@/pages/admin/Products').catch((err) => {
    handleLazyError(err, '@/pages/admin/Products');
    throw err;
  })
);
export const LazyAdminOrders = lazy(() =>
  import('@/pages/admin/Orders/index').catch((err) => {
    handleLazyError(err, '@/pages/admin/Orders/index');
    throw err;
  })
);

// ============= ROUTE PRELOADING STRATEGIES =============

// Preload critical routes on app initialization
export const preloadCriticalRoutes = () => {
  if (typeof window === 'undefined') return;
  
  // Preload login and marketplace on app start
  setTimeout(() => {
    import('@/pages/auth/Login').catch((err) => handleLazyError(err, '@/pages/auth/Login'));
    import('@/pages/Marketplace/UltraOptimizedBrowse').catch((err) =>
      handleLazyError(err, '@/pages/Marketplace/UltraOptimizedBrowse')
    );
  }, 2000);
};

// Preload admin routes when user is detected as admin
export const preloadAdminRoutes = () => {
  if (typeof window === 'undefined') return;
  
  setTimeout(() => {
    import('@/pages/admin/Dashboard').catch((err) => handleLazyError(err, '@/pages/admin/Dashboard'));
    import('@/pages/admin/Users/index').catch((err) => handleLazyError(err, '@/pages/admin/Users/index'));
  }, 1000);
};

// Preload marketplace detail when hovering over cards
export const preloadMarketplaceDetail = () => {
  import('@/pages/MarketplaceDetail').catch((err) =>
    handleLazyError(err, '@/pages/MarketplaceDetail')
  );
};

// ============= BUNDLE ANALYSIS =============

export const routeBundles = {
  auth: [
    'LazyLoginPage',
    'LazyCompleteProfilePage', 
    'LazyForgotPasswordPage',
    'LazyResetPasswordPage',
    'LazyPendingReviewPage',
    'LazyProfileRejectedPage',
    'LazyAccessRequestPage'
  ],
  core: [
    'LazyIndexPage',
    'LazyProfilePage',
    'LazyNotFoundPage',
    'LazyDebugPage'
  ],
  marketplace: [
    'LazyMarketplaceBrowse',
    'LazyMarketplaceUltraFastBrowse',
    'LazyMarketplaceDetail',
    'LazyMarketplaceSettings',
    'LazyProductDetails',
    'LazyOrderManagement'
  ],
  admin: [
    'LazyAdminDashboard',
    'LazyAdminUsers',
    'LazyAdminCultivars',
    'LazyAdminBatches',
    'LazyAdminHardware',
    'LazyAdminProducts',
    'LazyAdminOrders'
  ]
};